/**
 * Turning the grades and schools actually on file into filter options.
 *
 * Both live as free text in `students.metadata`, and the values are messy in ways that matter
 * here: `'TK'` and `'Incoming TK'` are one grade, `'College Park Elementary'` and
 * `'College Park Elementary School'` are one school. The teacher portal already knows this —
 * `normalizeGrade`, `gradeBand` and `shortSchoolLabel` are its rules — so this module reuses
 * them rather than inventing a second opinion.
 *
 * The output is deliberately a *label plus the raw values behind it*. A filter cannot ask the
 * database for "grade TK" when the column holds two spellings of it; it has to ask for both.
 * Normalising in the query would mean a function call per row on an unindexed jsonb field,
 * which is the wrong trade for a table that will grow.
 */

import {
  GRADE_BAND_LABEL,
  GRADE_BAND_ORDER,
  formatGradeLabel,
  gradeBand,
  gradeRank,
  normalizeGrade,
  shortSchoolLabel,
  type GradeBand,
} from './rosterGrouping';

/** A student as this module needs to see them. */
export interface FacetStudent {
  metadata: { grade?: string; school?: string } | null;
}

/**
 * One choice in a filter: what it is called, and every raw value that means it.
 *
 * `raw` is what reaches the query. It is never empty for a real option — an option nobody
 * could match would be a dead end in the list.
 */
export interface FacetOption {
  /** Stable value for the `<select>`, unique within its filter. */
  value: string;
  label: string;
  raw: string[];
}

/** A grade option, plus which band roll-up it sits under. */
export interface GradeFacet extends FacetOption {
  band: GradeBand;
  /** The `normalizeGrade` output — what the band and the sort order are computed from. */
  normalized: string;
}

export interface AttendanceFacets {
  /** Band roll-ups, in dismissal order, each carrying every raw grade beneath it. */
  bands: (FacetOption & { band: GradeBand })[];
  /** Individual grades, in TK → K → 1… order. */
  grades: GradeFacet[];
  schools: FacetOption[];
  /** True when at least one student has no grade on file — worth its own option. */
  hasMissingGrade: boolean;
  hasMissingSchool: boolean;
}

/** `'  Foster City  Elementary '` and `'Foster City Elementary'` are the same school. */
function tidy(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

/**
 * Build every filter option from the students on file.
 *
 * Options are derived from the data rather than from a fixed list, so a grade or school
 * spelled a new way shows up as soon as a student has it — and one that nobody has stops
 * cluttering the filter. That is also why the raw values are collected as we go: they are the
 * only reliable way back from a label to what the column actually holds.
 */
export function buildAttendanceFacets(students: readonly FacetStudent[]): AttendanceFacets {
  const gradeRawByNormalized = new Map<string, Set<string>>();
  const schoolRawByLabel = new Map<string, Set<string>>();
  let hasMissingGrade = false;
  let hasMissingSchool = false;

  for (const student of students) {
    const rawGrade = student.metadata?.grade;
    const normalized = normalizeGrade(rawGrade);
    if (normalized === null) {
      hasMissingGrade = true;
    } else {
      const set = gradeRawByNormalized.get(normalized) ?? new Set<string>();
      // The raw value, not the normalized one — this is what the column holds.
      set.add(tidy(rawGrade ?? ''));
      gradeRawByNormalized.set(normalized, set);
    }

    const rawSchool = tidy(student.metadata?.school ?? '');
    if (!rawSchool) {
      hasMissingSchool = true;
    } else {
      // Grouped by the short label, which is what merges "College Park Elementary" and
      // "College Park Elementary School" into the one school they obviously are.
      const label = shortSchoolLabel(rawSchool);
      const set = schoolRawByLabel.get(label) ?? new Set<string>();
      set.add(rawSchool);
      schoolRawByLabel.set(label, set);
    }
  }

  const grades: GradeFacet[] = [...gradeRawByNormalized.entries()]
    .map(([normalized, raw]) => ({
      value: `grade:${normalized}`,
      // "3rd Grade", "Kindergarten" — the same wording the pickup board uses for a heading.
      label: formatGradeLabel(normalized),
      raw: [...raw].sort(),
      band: gradeBand(normalized),
      normalized,
    }))
    // Sorted on the normalized value, never the label: TK → K → 1… is neither alphabetical
    // ("Kindergarten" before "TK") nor numeric ("10th" before "2nd").
    .sort((a, b) => gradeRank(a.normalized) - gradeRank(b.normalized));

  const bands = GRADE_BAND_ORDER.map(band => {
    const raw = grades.filter(g => g.band === band).flatMap(g => g.raw);
    return { value: `band:${band}`, label: GRADE_BAND_LABEL[band], raw, band };
  }).filter(option => option.raw.length > 0);

  const schools: FacetOption[] = [...schoolRawByLabel.entries()]
    .map(([label, raw]) => ({ value: `school:${label}`, label, raw: [...raw].sort() }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { bands, grades, schools, hasMissingGrade, hasMissingSchool };
}

/**
 * What a filter is currently asking for.
 *
 * `missing` is its own case rather than an empty `values` list: "no grade on file" is a real
 * question an admin asks — it is how you find the children a roster would quietly omit — and
 * it needs an `IS NULL`, not an `IN ()`.
 */
export type FacetSelection =
  | { kind: 'all' }
  | { kind: 'values'; raw: string[] }
  | { kind: 'missing' };

/** The sentinel a `<select>` uses for "no value on file". */
export const MISSING_VALUE = 'missing';

/** Resolve a `<select>` value back to what the query should ask for. */
export function facetSelectionFor(
  value: string,
  options: readonly FacetOption[],
): FacetSelection {
  if (value === 'all' || !value) return { kind: 'all' };
  if (value === MISSING_VALUE) return { kind: 'missing' };
  const match = options.find(option => option.value === value);
  // An unknown value degrades to "all" rather than to an empty result: a stale option is a
  // filter that silently shows nothing, which reads as missing data.
  return match ? { kind: 'values', raw: match.raw } : { kind: 'all' };
}
